import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Items() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    main_category_id: '',
    sub_category_id: '',
    description: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, mainCatRes, subCatRes] = await Promise.all([
        axios.get(`${API}/items`),
        axios.get(`${API}/main-categories`),
        axios.get(`${API}/sub-categories`)
      ]);
      setItems(itemsRes.data);
      setMainCategories(mainCatRes.data);
      setSubCategories(subCatRes.data);
    } catch (error) {
      toast.error('خطأ في جلب البيانات');
    }
  };

  const filteredSubCategories = subCategories.filter(
    sub => sub.main_category_id === newItem.main_category_id
  );

  const handleAdd = async () => {
    if (!newItem.name.trim() || !newItem.main_category_id || !newItem.sub_category_id) {
      toast.error('الرجاء إدخال اسم الصنف واختيار التصنيفات');
      return;
    }

    setLoading(true);
    try {
      const selectedMain = mainCategories.find(c => c.id === newItem.main_category_id);
      const selectedSub = subCategories.find(c => c.id === newItem.sub_category_id);
      
      await axios.post(`${API}/items`, {
        ...newItem,
        main_category_name: selectedMain.name,
        sub_category_name: selectedSub.name
      });
      
      toast.success('تم إضافة الصنف بنجاح');
      setNewItem({
        name: '',
        main_category_id: '',
        sub_category_id: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      // Check if it's a duplicate error
      if (error.response?.status === 409) {
        toast.error(error.response?.data?.detail || 'يوجد صنف بنفس الاسم مسبقاً');
      } else {
        toast.error('خطأ في إضافة الصنف');
      }
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/items/${id}`);
      toast.success('تم حذف الصنف بنجاح');
      fetchData();
    } catch (error) {
      toast.error('خطأ في حذف الصنف');
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/items/export/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'items.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('تم تصدير الأصناف بنجاح');
    } catch (error) {
      toast.error('خطأ في تصدير الأصناف');
    }
  };

  const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('يجب أن يكون الملف Excel (.xlsx أو .xls)');
      return;
    }
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/items/import/excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const { imported, skipped, errors } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(`تم استيراد ${imported} صنف. تم تخطي ${skipped} صنف`, {
          description: errors.slice(0, 3).join('\n'),
          duration: 5000
        });
      } else {
        toast.success(`تم استيراد ${imported} صنف بنجاح`);
      }
      
      fetchData();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'خطأ في استيراد الملف');
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-slate-100 p-3 sm:p-6">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-5 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-800 mb-1">
              إدارة الأصناف
            </h1>
            <p className="text-xs sm:text-sm text-slate-600">أضف وأدر الأصناف مع تصنيفاتها</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleExportExcel}
              className="flex-1 sm:flex-none gap-1.5 bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-9"
              title="تصدير الأصناف إلى Excel"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="hidden sm:inline">تصدير</span> Excel
            </Button>
            
            <Button 
              onClick={() => document.getElementById('import-excel').click()}
              disabled={importing}
              className="flex-1 sm:flex-none gap-1.5 bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm h-9"
              title="استيراد الأصناف من Excel"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {importing ? 'جاري...' : <><span className="hidden sm:inline">استيراد</span> Excel</>}
            </Button>
            <input
              id="import-excel"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportExcel}
              className="hidden"
            />
            
            <Button 
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/procurement')} 
              variant="outline"
              className="gap-1.5 text-xs sm:text-sm h-9"
            >
              <ArrowLeft className="w-4 h-4 shrink-0" />
              <span>العودة</span>
            </Button>
          </div>
        </div>

        <Card className="mb-4 sm:mb-6 border-0 shadow-lg bg-white">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg">إضافة صنف جديد</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label htmlFor="item-name" className="text-xs sm:text-sm">اسم الصنف</Label>
                <Input
                  id="item-name"
                  data-testid="item-name-input"
                  placeholder="مثال: أسمنت، حديد، بلاط"
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                  className="mt-1.5 h-9 sm:h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">التصنيف الرئيسي</Label>
                <Select 
                  value={newItem.main_category_id} 
                  onValueChange={(value) => setNewItem({...newItem, main_category_id: value, sub_category_id: ''})}
                >
                  <SelectTrigger data-testid="main-category-select" className="mt-1.5 h-9 sm:h-10 text-sm">
                    <SelectValue placeholder="اختر التصنيف الرئيسي" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {mainCategories.map(category => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs sm:text-sm">التصنيف الفرعي</Label>
                <Select 
                  value={newItem.sub_category_id} 
                  onValueChange={(value) => setNewItem({...newItem, sub_category_id: value})}
                  disabled={!newItem.main_category_id}
                >
                  <SelectTrigger data-testid="sub-category-select" className="mt-1.5 h-9 sm:h-10 text-sm">
                    <SelectValue placeholder="اختر التصنيف الفرعي" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {filteredSubCategories.map(category => (
                      <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="item-description" className="text-xs sm:text-sm">وصف الصنف (اختياري)</Label>
                <Textarea
                  id="item-description"
                  data-testid="item-description-input"
                  placeholder="وصف تفصيلي للصنف"
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                  className="mt-1.5 text-sm"
                  rows={2}
                />
              </div>
            </div>
            <Button 
              data-testid="add-item-btn"
              onClick={handleAdd} 
              disabled={loading}
              className="w-full mt-4 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:shadow-lg h-9 sm:h-10 text-sm"
            >
              <Plus className="w-4 h-4 ml-1.5" />
              إضافة الصنف
            </Button>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-white">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg">قائمة الأصناف ({items.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-6 pt-0">
            {items.length === 0 ? (
              <p className="text-center text-slate-500 py-6 text-sm">لا توجد أصناف. قم بإضافة صنف جديد</p>
            ) : (
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="w-full min-w-[500px] text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200 bg-slate-50">
                      <th className="text-right p-2 sm:p-3 font-semibold text-slate-700">اسم الصنف</th>
                      <th className="text-right p-2 sm:p-3 font-semibold text-slate-700">التصنيف الرئيسي</th>
                      <th className="text-right p-2 sm:p-3 font-semibold text-slate-700">التصنيف الفرعي</th>
                      <th className="text-right p-2 sm:p-3 font-semibold text-slate-700 hidden sm:table-cell">الوصف</th>
                      <th className="text-center p-2 sm:p-3 font-semibold text-slate-700 w-16">حذف</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id} data-testid={`item-row-${item.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-2 sm:p-3 text-slate-800 font-medium">{item.name}</td>
                        <td className="p-2 sm:p-3">
                          <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] sm:text-xs">
                            {item.main_category_name || item.category_name || '-'}
                          </span>
                        </td>
                        <td className="p-2 sm:p-3">
                          <span className="inline-block px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] sm:text-xs">
                            {item.sub_category_name || '-'}
                          </span>
                        </td>
                        <td className="p-2 sm:p-3 text-slate-600 hidden sm:table-cell">{item.description || '-'}</td>
                        <td className="p-2 sm:p-3 text-center">
                          <Button
                            data-testid={`delete-item-${item.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
